import React, { useEffect, useState } from 'react';
import { Alert, Box, Chip, CircularProgress, IconButton, InputAdornment, Link, TextField, Tooltip, Typography } from '@mui/material';
import { Close, ImageOutlined, BrokenImageOutlined, CloudDone } from '@mui/icons-material';
import { normalizeImageUrl, validateImageUrl, isStoredServiceImage } from '../../utils/imageUrl';

export type ImageLoadState = 'empty' | 'loading' | 'loaded' | 'error';

interface ServiceImageFieldProps {
  value?: string;
  /** The original link a stored copy was made from, shown for reference. */
  sourceUrl?: string;
  onChange: (value: string) => void;
  /** Reports whether the current URL actually loads, so the form can block a broken save. */
  onStatusChange?: (state: ImageLoadState) => void;
}

/**
 * Image URL input for a service, with a live preview at the aspect ratio the
 * app's service tiles use, so the admin sees roughly how it will be cropped.
 */
const ServiceImageField: React.FC<ServiceImageFieldProps> = ({ value, sourceUrl, onChange, onStatusChange }) => {
  const url = normalizeImageUrl(value);
  const stored = isStoredServiceImage(url);
  const formatError = validateImageUrl(url);
  const [state, setState] = useState<ImageLoadState>(url ? 'loading' : 'empty');

  // Re-check whenever the URL changes. Only a well-formed https URL is tried;
  // a malformed one is reported by the text field instead.
  useEffect(() => {
    if (!url) {
      setState('empty');
      return;
    }
    if (formatError) {
      setState('error');
      return;
    }
    setState('loading');
    const img = new Image();
    let cancelled = false;
    img.onload = () => !cancelled && setState('loaded');
    img.onerror = () => !cancelled && setState('error');
    img.src = url;
    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [url, formatError]);

  useEffect(() => {
    onStatusChange?.(state);
  }, [state, onStatusChange]);

  const helper =
    formatError ??
    (state === 'error'
      ? "This link didn't load an image. Check it opens the picture itself, not a web page."
      : stored
        ? 'This photo is stored in PickupNinja storage. Paste a new link to replace it.'
        : url
          ? 'When you save, a copy is stored in PickupNinja storage, so the app keeps working if this site removes the image.'
          : 'Optional. Paste a direct link to a photo (it should end in .jpg, .png or .webp, or come from an image host).');

  return (
    <Box>
      <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>
        Service Image
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
        <Box sx={{ flex: 1, minWidth: 260 }}>
          <TextField
            fullWidth
            label="Image URL"
            placeholder="https://images.unsplash.com/…"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            error={!!formatError || state === 'error'}
            helperText={helper}
            InputProps={{
              endAdornment: url ? (
                <InputAdornment position="end">
                  <Tooltip title="Remove image">
                    <IconButton size="small" onClick={() => onChange('')} aria-label="Remove image">
                      <Close fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ) : undefined,
            }}
          />
          {stored && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip size="small" color="success" variant="outlined" icon={<CloudDone />} label="Stored copy" />
              {sourceUrl && (
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                  Copied from{' '}
                  <Link href={sourceUrl} target="_blank" rel="noopener noreferrer">
                    {sourceUrl.length > 60 ? `${sourceUrl.slice(0, 60)}…` : sourceUrl}
                  </Link>
                </Typography>
              )}
            </Box>
          )}
          <Alert severity="info" variant="outlined" sx={{ mt: 1.5, py: 0 }}>
            Only use photos you're allowed to use, with no other companies' logos, names or
            product branding visible (delivery bags, uniforms, tools). Check the picture at
            full size before saving.
          </Alert>
        </Box>

        {/* Preview at the app tile's aspect ratio (≈4:3, cropped to fill) */}
        <Box
          sx={{
            width: 200,
            flexShrink: 0,
            aspectRatio: '4 / 3',
            borderRadius: 3,
            overflow: 'hidden',
            bgcolor: 'grey.100',
            border: 1,
            borderColor: state === 'error' ? 'error.light' : 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.secondary',
          }}
        >
          {state === 'loaded' && (
            <Box component="img" src={url} alt="Service preview" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          )}
          {state === 'loading' && <CircularProgress size={24} />}
          {state === 'empty' && (
            <Box sx={{ textAlign: 'center' }}>
              <ImageOutlined />
              <Typography variant="caption" display="block">No image — the app shows the icon</Typography>
            </Box>
          )}
          {state === 'error' && (
            <Box sx={{ textAlign: 'center', color: 'error.main' }}>
              <BrokenImageOutlined />
              <Typography variant="caption" display="block">Can't load</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ServiceImageField;
